import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, Button } from '@mui/material';
import { 
  Lightbulb, 
  People, 
  Timeline,
  CheckCircle,
  ArrowForward
} from '@mui/icons-material';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import '../styles/LandingPage.css';

function AboutPage() {
  const navigate = useNavigate();

  const handleContactUs = () => {
    navigate('/contact');
  };

  return (
    <div className="AboutPage">
      <Header />
      {/* Our Mission Section */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Lightbulb sx={{ fontSize: 48, color: '#5090D3' }} />
            </Box>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#0B1929',
                mb: 4
              }}
            >
              Our Mission
            </Typography>
          </Box>
          
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12}>
              <Typography 
                variant="h5" 
                align="center" 
                sx={{ 
                  fontWeight: 600,
                  color: '#0B1929',
                  maxWidth: '900px',
                  mx: 'auto',
                  mb: 4,
                  lineHeight: 1.4
                }}
              >
                To democratise AI capabilities across the insurance industry, enabling every participant—from boutique brokers to global syndicates—to harness the power of their data.
              </Typography>
              
              <Typography 
                variant="body1" 
                align="center" 
                sx={{ 
                  color: '#64748B',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontSize: '1.125rem',
                  lineHeight: 1.8
                }}
              >
                We believe that access to sophisticated data intelligence shouldn't be limited to organisations with large technical teams. By building Decode, we're ensuring that every insurance industry participant can transform their operational data into strategic advantage, automate compliance workflows, and make decisions with confidence—regardless of their size or technical resources.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {[
              {
                title: "Eliminate Data Chaos",
                description: "Transform unstandardised bordereaux and reports into clean, analysable assets in minutes, not days"
              },
              {
                title: "Empower Every User",
                description: "Enable non-technical staff to perform complex analyses through natural language queries and AI assistance"
              },
              {
                title: "Future-Proof Operations",
                description: "Position the insurance industry to leverage emerging AI capabilities while maintaining regulatory compliance"
              }
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: '#fff',
                    border: '1px solid #E4E7EB',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(80, 144, 211, 0.15)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <CheckCircle sx={{ color: '#10B981', mb: 2 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        color: '#0B1929',
                        mb: 2
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ backgroundColor: '#fff', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <People sx={{ fontSize: 48, color: '#5090D3' }} />
            </Box>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#0B1929',
                mb: 4
              }}
            >
              Our Team
            </Typography>
          </Box>

          <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748B',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                mb: 4
              }}
            >
              BIM Analytics brings together expertise in AI, insurance technology, and enterprise software to solve the insurance industry's most pressing data challenges.
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748B',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                mb: 4
              }}
            >
              Our founding team combines deep technical knowledge with hands-on experience in insurance operations. We've built enterprise AI systems, developed data architectures for Fortune 500 companies, and understand the unique complexities of data workflows.
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748B',
                fontSize: '1.125rem',
                lineHeight: 1.8
              }}
            >
              Working closely with our design partner, a specialty MGA in the Lloyd\u2019s market, we ensure every feature we build solves real operational challenges. This collaborative approach, combined with our technical expertise, enables us to deliver solutions that are both cutting-edge and practical.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Our Journey Section */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Timeline sx={{ fontSize: 48, color: '#5090D3' }} />
            </Box>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#0B1929',
                mb: 4
              }}
            >
              Our Journey
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  color: '#0B1929',
                  mb: 3
                }}
              >
                Where We Started
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748B',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 3
                }}
              >
                BIM Analytics began when we recognised a fundamental problem: insurance organisations were drowning in data but starving for insights. Despite the promise of AI transformation, most organisations couldn't access these capabilities because their data remained trapped in unstandardised formats.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748B',
                  fontSize: '1.05rem',
                  lineHeight: 1.8
                }}
              >
                We saw underwriting teams spending 40% of their time on manual data reconciliation, compliance officers checking spreadsheets line by line, and valuable insights hidden in siloed systems. We knew there had to be a better way.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  color: '#0B1929',
                  mb: 3
                }}
              >
                Where We're Going
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748B',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 3
                }}
              >
                Today, Decode is transforming how companies work with data. Our platform already automates bordereaux processing, enables natural language analytics, and brings your own AI co-worker directly into everyday workflows through Microsoft Teams and Slack.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748B',
                  fontSize: '1.05rem',
                  lineHeight: 1.8
                }}
              >
                Looking ahead, we're building towards a future where every company has an AI-powered data scientist at their fingertips. We're expanding our compliance tool library, developing predictive models from aggregated market data, and creating new ways for the market to collaborate and share intelligence—all while maintaining the security and trust that the industry demands.
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 8, p: 4, backgroundColor: '#fff', borderRadius: 2, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: '#5090D3',
                mb: 2
              }}
            >
              Join Us in Transforming the Insurance Industry
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748B',
                maxWidth: '600px',
                mx: 'auto',
                mb: 4
              }}
            >
              We're partnering with forward-thinking Lloyd's market participants who are ready to embrace the future of insurance intelligence. Together, we're not just solving today's challenges—we're building the foundation for tomorrow's opportunities.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleContactUs}
              className="cta-button-primary"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                py: 2,
                px: 5,
                borderRadius: '10px',
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(80, 144, 211, 0.3)',
                backgroundColor: '#5090D3',
                '&:hover': {
                  backgroundColor: '#4080C3',
                }
              }}
            >
              Contact Us
              <ArrowForward sx={{ ml: 1, fontSize: '1.2rem' }} />
            </Button>
          </Box>
        </Container>
      </Box>
    </div>
  );
}

export default AboutPage;